import React from 'react';
import { StyleSheet } from 'react-native';
import { FileType } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNACard, DNAText, DNAButton, Iffy } from '@alucio/lux-ui';
import {
  DNAChip,
  DNADocumentThumbnail,
  DNADocumentContextMenu,
  DNARowsComponent,
  DNADocumentDefaultRowProps,
  useDNADocumentActions,
} from 'src/components/DNA';
import { DocumentORM } from 'src/types/types';

const styles = StyleSheet.create({
  container: {
    paddingVertical: 12,
  },
  unavailableContainer: {
    opacity: 0.4,
    paddingVertical: 12,
  },
});

const DNADocumentDashboardRow: DNARowsComponent<DocumentORM, DNADocumentDefaultRowProps> = (props) => {
  const { ORM: documentORM, isOnline, onPin, variant } = props
  const hideControls = variant === 'dashboardFavorites'
  const isBookmarked = documentORM.meta.bookmark.isBookmarked;
  const documentActions = useDNADocumentActions();
  const documentVersionORM = documentORM.relations.version.latestPublishedDocumentVersionORM ||
    documentORM.relations.version.latestDocumentVersionORM;
  const isContentAvailable = isOnline ||
    (documentVersionORM?.meta.assets.isContentCached && documentVersionORM.model.type !== FileType.WEB);

  const onBookmark = (): void => {
    documentActions.bookmark(documentORM)()
    onPin?.()
  };

  const onPress = (): void => {
    isContentAvailable && props.onPress?.(documentORM);
  };

  return (
    <DNACard
      appearance="flat"
      interactive="pressable"
      onPress={onPress}
      style={styles.container}
    >
      <DNABox
        style={!isContentAvailable && styles.unavailableContainer}
        testID="dashboard-document-row"
        alignY="center"
        spacing="between"
        childFill={0}
      >
        <DNABox alignY="center" spacing="md" childFill={1}>
          <DNABox appearance="row" alignY="center" spacing="sm">
            {/* BOOKMARK */}
            <Iffy is={!hideControls}>
              <DNAButton
                appearance="ghost"
                status={isBookmarked ? 'primary' : 'subtle'}
                iconLeft={isBookmarked ? 'star-fill' : 'star'}
                onPress={onBookmark}
              />
            </Iffy>
            {/* THUMBNAIL */}
            <DNADocumentThumbnail.Hover
              documentVersionORM={documentVersionORM}
              width={120}
              height={68}
              unavailableContent={!isContentAvailable}
              showProcessing={isContentAvailable}
            />
          </DNABox>
          <DNABox appearance="col" spacing="sm">
            {/* TITLE */}
            <DNAText testID={`document-row-name-${documentVersionORM.model.title}`} b1 numberOfLines={1}>
              {documentVersionORM.model.title}
            </DNAText>
            {/* UNAVAILABLE CONTENT BADGE */}
            <Iffy is={!isContentAvailable}>
              <DNABox>
                <DNAChip.Beacon
                  testID={`content-not-available-${documentORM.model.id}`}
                  variant="CONTENT_UNAVAILABLE"
                />
              </DNABox>
            </Iffy>
          </DNABox>
        </DNABox>
        <Iffy is={!hideControls}>
          <DNABox spacing="sm" alignY="center">
            {/* ELLIPSIS */}
            <DNADocumentContextMenu documentORM={documentORM}>
              <DNAButton
                appearance="ghost"
                status="subtle"
                iconLeft="dots-three-vertical-bold"
              />
            </DNADocumentContextMenu>
          </DNABox>
        </Iffy>
      </DNABox>
    </DNACard>
  )
}

export default DNADocumentDashboardRow
